import './index.css'

import { MdPhotoSizeSelectLarge } from '@react-icons/all-files/md/MdPhotoSizeSelectLarge'
import isEmpty from 'lodash/isEmpty'
import uniq from 'lodash/uniq'
import without from 'lodash/without'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  COPY_METADATA_SOURCE,
  COPY_METADATA_TARGET,
} from '@disco/constants/ItemTypes'
import {
  copyMetadata,
  setCopyMetadata,
} from '@disco/state-management/actions/copy-metadata-actions'
import { fetchTrackMetadata } from '@disco/state-management/actions/ServerActions'
import type { CopyMetadataState } from '@disco/state-management/reducers/copy-metadata'
import {
  selectCopyMetadataChanges,
  selectCopyMetadataSourceFields,
  selectCopyMetadataSourceTrack,
  selectCopyMetadataState,
  selectCopyMetadataTargetTracks,
} from '@disco/state-management/selectors/copy-metadata'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'
import { getTracks } from '@disco/state-management/selectors/tracks'
import { getVisibleCustomFieldsKeys } from '@disco/state-management/selectors/user'
import { plural } from '@disco/utils/string-utils'

import type { CopyMetadataFields } from 'types/copy-metadata'
import type { Track, TrackId } from 'types/track'

import { getShortlistBaseRoute } from 'utils/routing'

import CopyMetadataTrack from 'components/common/copy-metadata-track'
import Row from 'components/common/row'
import TracksSelector from 'components/common/tracks-selector'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import DropZone from 'components/ui/drop-zone'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import InlineLink from 'components/ui/inline-link'
import ModalDialog from 'components/ui/modal-dialog'
import ModalWindow from 'components/ui/modal-window'

import useLocalStorage from 'hooks/use-local-storage'
import { useDispatch, useSelector } from 'hooks/use-redux'

const METADATA_FIELDS_KEYS = [
  'Album',
  'Artist',
  'BPM',
  'Comment',
  'Composer',
  'Genre',
  'Grouping',
  'ISRC',
  'Lyrics',
  'ProNumber',
  'Release Date',
  'Title',
  'TrackNumber',
  'Year',
]
const DISABLED_METADATA_FIELDS_KEYS = ['Title', 'TrackNumber']

const CopyMetadata = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { sourceTrackId, targetTrackIds }: CopyMetadataState = useSelector(
    selectCopyMetadataState,
  )
  const location = useSelector(getLocationBeforeTransitions)
  // const corePlan: ?Plan = useSelector(getCorePlan)
  const customFieldsKeys: string[] = useSelector(getVisibleCustomFieldsKeys)
  const sourceTrack: Track | null | undefined = useSelector(
    selectCopyMetadataSourceTrack,
  )
  const targetTracks: Track[] = useSelector(selectCopyMetadataTargetTracks)
  const tracks: Record<TrackId, Track> = useSelector(getTracks)
  const trackIds: TrackId[] = useMemo(
    () => [sourceTrackId, ...targetTrackIds].filter(Boolean),
    [sourceTrackId, targetTrackIds],
  )
  const [loadedTracks, setLoadedTracks] = useState<TrackId[]>([])
  useEffect(() => {
    const loadTrack = async (id: TrackId) => {
      try {
        await dispatch(fetchTrackMetadata(id))
        setLoadedTracks(state => [...state, id])
      } catch (error) {}
    }

    for (const id of trackIds) {
      if (!loadedTracks.includes(id)) {
        loadTrack(id)
      }
    }
  }, [trackIds])
  const [confirmationDismissed, setConfirmationDismissed] =
    useLocalStorage<boolean>('copy_metadata_confirmation_dismissed', false)
  const [showConfirmationDialog, setShowConfirmationDialog] =
    useState<boolean>(false)
  const [selectedFields, setSelectedFields] = useState<CopyMetadataFields>(
    {} as CopyMetadataFields,
  )
  useEffect(() => {
    if (sourceTrack) {
      setSelectedFields({
        artwork: sourceTrack.artwork ? true : false,
        comments_indexes: (sourceTrack.comments ?? []).reduce<
          Record<number, boolean>
        >(
          (accumulator, item, index) => ({ ...accumulator, [index]: true }),
          {},
        ),
        metadata_keys: [...METADATA_FIELDS_KEYS, ...customFieldsKeys].reduce<
          Record<string, boolean>
        >(
          (accumulator, key) =>
            sourceTrack.metadata[key]
              ? {
                  ...accumulator,
                  [key]: DISABLED_METADATA_FIELDS_KEYS.includes(key)
                    ? false
                    : true,
                }
              : accumulator,
          {},
        ),
        native_tags_indexes: (sourceTrack.native_tags ?? []).reduce<
          Record<number, boolean>
        >(
          (accumulator, item, index) => ({ ...accumulator, [index]: true }),
          {},
        ),
        track_writers_indexes: (sourceTrack.track_writers ?? []).reduce<
          Record<number, boolean>
        >(
          (accumulator, item, index) => ({ ...accumulator, [index]: true }),
          {},
        ),
      })
    }
  }, [sourceTrack])
  const overrides = useSelector(state =>
    selectCopyMetadataSourceFields(state, selectedFields),
  )
  const copyMetadataChanges = useSelector(state =>
    selectCopyMetadataChanges(state, selectedFields),
  )
  useEffect(() => {
    return () => {
      dispatch(
        setCopyMetadata({
          sourceTrackId: null,
          targetTrackIds: [],
          returnPath: undefined,
        }),
      )
    }
  }, [])
  const hideConfirmation = useCallback(() => {
    setShowConfirmationDialog(false)
  }, [])
  const showConfirmation = useCallback(() => {
    setShowConfirmationDialog(true)
  }, [])
  const handleClose = useCallback(() => {
    const baseRoute = getShortlistBaseRoute(location)
    navigate(`${baseRoute || '/'}`)
  }, [navigate, location])
  const handleSubmit = useCallback(() => {
    dispatch(copyMetadata(selectedFields))
    const baseRoute = getShortlistBaseRoute(location)
    navigate(`${baseRoute || '/'}`)
  }, [dispatch, selectedFields, navigate])

  const renderEmptyState = (pane: 'source' | 'target') => (
    <div className="CopyMetadata-drop">
      <div className="CopyMetadata-drop-icon">
        <MdPhotoSizeSelectLarge width={24} height={24} />
      </div>
      {pane === 'source' && (
        <span>Search for tracks or drag tracks from the right column.</span>
      )}
      {pane === 'target' && (
        <span>Search for tracks or drag tracks from the left column.</span>
      )}
    </div>
  )

  return showConfirmationDialog ? (
    <ModalDialog
      title="Warning"
      primaryAction={{
        caption: 'Proceed',
        onClick: handleSubmit,
      }}
      status="warning"
      onCancel={hideConfirmation}
      onDismiss={() => setConfirmationDismissed(true)}
    >
      <p>
        You’re about to overwrite some existing metadata fields on the following
        tracks:
      </p>
      <ul>
        {Object.keys(copyMetadataChanges).map((key: string) => (
          <li key={key}>
            {tracks[Number(key)].name} (
            {plural(copyMetadataChanges[key], 'field', 'fields')} overwritten)
          </li>
        ))}
      </ul>
      <p>This can’t be undone. Are you sure you want to proceed?</p>
    </ModalDialog>
  ) : (
    <ModalWindow
      header={
        <>
          <Heading size={700}>Copy track info between tracks</Heading>
          <div className="form-note">
            Save time on metadata entry by copying track info between your
            tracks.{' '}
            <InlineLink
              to="https://support.disco.ac/home/copying-track-info-between-tracks"
              rel="noopener noreferrer"
              target="_blank"
            >
              Find out more.
            </InlineLink>
          </div>
        </>
      }
      actions={
        <ButtonContainer>
          <Button caption="Cancel" onClick={handleClose} />
          <Button
            appearance="primary"
            disabled={!sourceTrackId || targetTrackIds.length === 0}
            caption={
              targetTrackIds.length > 0
                ? `Copy to ${plural(targetTrackIds.length, 'track', 'tracks')}`
                : 'Copy track info'
            }
            onClick={
              confirmationDismissed || isEmpty(copyMetadataChanges)
                ? handleSubmit
                : showConfirmation
            }
          />
        </ButtonContainer>
      }
      isOpen={true}
      width="large"
      onClose={handleClose}
    >
      <DocumentHeadTags title="Copy track info | DISCO" />
      <div className="CopyMetadata">
        <div className="CopyMetadata-pane">
          <Row columnsSize={[12]}>
            <div>
              <Heading size={500}>From</Heading>
              <TracksSelector
                excludeIds={trackIds}
                initialQuery={
                  targetTracks.length === 1 ? targetTracks[0].name : undefined
                }
                placeholder="Search for a track to copy track info from"
                onSelect={(ids: TrackId[]) =>
                  dispatch(
                    setCopyMetadata({
                      sourceTrackId: ids[0],
                    }),
                  )
                }
              />
            </div>
          </Row>
          <DropZone
            accept={COPY_METADATA_TARGET}
            className="CopyMetadata-container"
            onDrop={(id: TrackId) => {
              setLoadedTracks(state => without(state, sourceTrackId))
              dispatch(
                setCopyMetadata({
                  sourceTrackId: id,
                  targetTrackIds: without(targetTrackIds, id),
                }),
              )
            }}
          >
            {sourceTrackId ? (
              <div className="CopyMetadata-container-item">
                <CopyMetadataTrack
                  expanded
                  loading={!loadedTracks.includes(sourceTrackId)}
                  selectable
                  selectedFields={selectedFields}
                  trackId={sourceTrackId}
                  onSelect={setSelectedFields}
                  onRemoveClick={(id: TrackId) => {
                    setLoadedTracks(state => without(state, id))
                    dispatch(
                      setCopyMetadata({
                        sourceTrackId: null,
                      }),
                    )
                  }}
                />
              </div>
            ) : (
              renderEmptyState('source')
            )}
          </DropZone>
        </div>
        <div className="CopyMetadata-arrow">
          <Icon type="arrow-right-big-v2" />
        </div>
        <div className="CopyMetadata-pane">
          <Row columnsSize={[12]}>
            <div>
              <Heading size={500}>To</Heading>
              <TracksSelector
                excludeIds={trackIds}
                initialQuery={sourceTrack?.name}
                multiple
                placeholder="Search for a track to copy track info to"
                onSelect={(ids: TrackId[]) =>
                  dispatch(
                    setCopyMetadata({
                      targetTrackIds: uniq([...targetTrackIds, ...ids]),
                    }),
                  )
                }
              />
            </div>
          </Row>
          <DropZone
            accept={COPY_METADATA_SOURCE}
            className="CopyMetadata-container"
            onDrop={(id: TrackId) => {
              dispatch(
                setCopyMetadata({
                  sourceTrackId: null,
                  targetTrackIds: uniq([id, ...targetTrackIds]),
                }),
              )
            }}
          >
            {targetTrackIds.length > 0
              ? targetTrackIds.map((id: TrackId) => (
                  <div className="CopyMetadata-container-item" key={id}>
                    <CopyMetadataTrack
                      loading={!loadedTracks.includes(id)}
                      overrides={overrides}
                      selectedFields={selectedFields}
                      trackId={id}
                      onRemoveClick={(id: TrackId) => {
                        setLoadedTracks(state => without(state, id))
                        dispatch(
                          setCopyMetadata({
                            targetTrackIds: without(targetTrackIds, id),
                          }),
                        )
                      }}
                    />
                  </div>
                ))
              : renderEmptyState('target')}
          </DropZone>
        </div>
      </div>
    </ModalWindow>
  )
}

export default CopyMetadata
